/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Spacings, Fonts } from '@styles/index';
import P2 from '@base/P2/P2';
import H5 from '@base/H5/H5';
import { IConceptTopic } from 'types/notes';
import P1 from '@base/P1/P1';
import Menu from '@base/Menu/Menu';
import KeyboardArrowDown from '@icon/notes/KeyboardArrowDown';

interface ICSSProps {
  $disabled: boolean;
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_1B};
  cursor: pointer;
  width: max-content;
`;

interface ITopicsDropdownProps {
  label: string;
  value: string;
  topics: IConceptTopic[];
  onChangeTopic: (uid: string) => void;
}

const StyH5 = styled(H5)`
  font-weight: ${Fonts.SEMIBOLD};
  line-height: ${Spacings.SPACING_5B};
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 248px;
  overflow: hidden;
`;

const StyIcon = styled(KeyboardArrowDown)`
  cursor: pointer;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 400ms ease-in-out;
`;

const StyMenu = styled(Menu)`
  margin-top: 14px;

  &.MuiPopover-root .MuiPaper-root {
    max-width: 272px;
    min-width: 270px;
    margin-top: -2px;
  }

  &.MuiPopover-root .MuiMenu-list {
    padding-top: ${Spacings.SPACING_3B};
    padding-bottom: ${Spacings.SPACING_3B};
  }
`;

const MenuItem = styled.li<ICSSProps>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_4B};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  gap: ${Spacings.SPACING_1B};
  user-select: none;
  &:hover {
    background-color: ${({ $disabled }) =>
      $disabled ? 'none' : 'var(--color-divider)'};
  }
`;

const StyP1 = styled(P1)`
  font-weight: ${Fonts.SEMIBOLD};
  line-height: 18px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const StyP2 = styled(P2)`
  font-weight: ${Fonts.LIGHT};
  line-height: 18px;
`;

const InnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SoonTag = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-divider);
  border-radius: ${Spacings.SPACING_1B};
  text-transform: uppercase;
  font-weight: ${Fonts.HEAVY};
  font-size: ${Fonts.EXTRA_SMALL};
  line-height: 10px;
  color: var(--color-text-secondary);
  height: 14px;
  min-width: 42px;
`;

const TopicsDropdown: React.FC<ITopicsDropdownProps> = ({
  label,
  value,
  onChangeTopic,
  topics = []
}) => {
  const [anchor, setAnchor] = useState(null);
  const ref = useRef();

  const onDropdownClick = (e) => {
    setAnchor(ref.current);
  };
  const onDropdownClose = () => setAnchor(null);
  const onMenuItemClick = (
    uid,
    childrenCount,
    isEpubAvailable,
    isNotesAvailable
  ) => {
    if (childrenCount > 0 && (isEpubAvailable || isNotesAvailable)) {
      onChangeTopic(uid);
      onDropdownClose();
    }
  };

  const topicsList = useMemo(() => {
    const selectedTopic = topics.filter((topic) => value === topic.uid);
    const filteredTopics = topics.filter((topic) => value !== topic.uid);
    return [...selectedTopic, ...filteredTopics];
  }, [topics]);

  return (
    <>
      <Wrapper ref={ref} onClick={onDropdownClick}>
        <StyH5 color="var(--color-text-primary)">{label}</StyH5>
        <StyIcon
          color="var(--color-text-primary)"
          height={Spacings.SPACING_6B}
          width={Spacings.SPACING_6B}
          $open={!!anchor}
        />
      </Wrapper>
      <StyMenu anchorEl={anchor} onClose={onDropdownClose}>
        <>
          {topicsList.map(
            ({
              childrenCount,
              uid,
              name,
              isEpubAvailable,
              isNotesAvailable
            }) => (
              <MenuItem
                onClick={() =>
                  onMenuItemClick(
                    uid,
                    childrenCount,
                    isEpubAvailable,
                    isNotesAvailable
                  )
                }
                $disabled={
                  !childrenCount || (!isEpubAvailable && !isNotesAvailable)
                }
                key={uid}
              >
                <InnerWrapper>
                  <StyP1
                    color={
                      !childrenCount || (!isEpubAvailable && !isNotesAvailable)
                        ? 'var(--color-text-secondary)'
                        : 'var(--color-text-primary)'
                    }
                  >
                    {name}
                  </StyP1>
                  {!isEpubAvailable && !isNotesAvailable ? (
                    <SoonTag>SOON</SoonTag>
                  ) : null}
                </InnerWrapper>
                {isEpubAvailable || isNotesAvailable ? (
                  <StyP2 color="var(--color-text-secondary)">
                    {childrenCount}{' '}
                    {childrenCount === 1 ? 'chapter' : 'chapters'}
                  </StyP2>
                ) : null}
              </MenuItem>
            )
          )}
        </>
      </StyMenu>
    </>
  );
};

export default TopicsDropdown;
